const Consts = {
    HIGH: 1,
    LOW: 0,
    OUTPUT: 1,
    INPUT: 0,
    PULLUP: 1,
    NO_PULLS: 0,

    // Konashi I/0 pin
    PIO0: 0,
    PIO1: 1,
    PIO2: 2,
    PIO3: 3,
    PIO4: 4,

    PWM0: 0,
    PWM1: 1,
    PWM2: 2,
    PWM3: 3,

    // Konashi PWM
    KONASHI_PWM_DISABLE: 0,
    KONASHI_PWM_ENABLE: 1,
    KONASHI_PWM_ENABLE_LED_MODE: 2,
    KONASHI_PWM_LED_PERIOD: 10000 // 10ms
};

class Konashi {
    /**
   * Create konashi UUID
   *
   * @param {string} part 4 characters hex
   * @returns {string} konashi UUID including the given part
   */
    static _createUUID (part) {
        return `229b${part}-03fb-40da-98a7-b0def65c2d4b`;
    }

    /**
   * Returns konashi's service UUID
   *
   * @returns {string} konashi service UUID
   */
    static get _serviceUUID () {
        return Konashi._createUUID('ff00');
    }

    /**
   * Returns konashi's UUID with label
   *
   * @returns {object<string, string>} key: label, value: UUID
   */
    static get _c12cUUIDs () {
        return {
            pioSetting: Konashi._createUUID('3000'),
            pioPullUp: Konashi._createUUID('3001'),
            pioOutput: Konashi._createUUID('3002'),
            pioInputNotification: Konashi._createUUID('3003'),
            pwmConfig: Konashi._createUUID('3004'),
            pwmParameter: Konashi._createUUID('3005'),
            pwmDuty: Konashi._createUUID('3006'),
            hardPwmConfig: Konashi._createUUID('3007'),
            hardPwmDuty: Konashi._createUUID('3008'),
            hardPwmDutyAll: Konashi._createUUID('3009'),
            hardPwmDutyTime: Konashi._createUUID('3010'),
            softwareReset: Konashi._createUUID('3013'),
            hardwareReset: Konashi._createUUID('3014'),
            hardwareLowBatteryNotification: Konashi._createUUID('3015')
        };
    }

    /**
   * Find konasih2 device
   *
   * @param {boolean} [autoConnect] connect automatically after the device is found (default: true)
   * @param {string} name device name prefix used to filter devices
   * @param {object} [options] defaul:
   *     `{filters: [{namePrefix: 'konashi2'}], optionalServices: ['229bff00-03fb-40da-98a7-b0def65c2d4b']}`
   * @param {function} failedCallback called when the connection fails
   * @returns {Promise<Konashi>} Promise resolved with the found Konashi instance
   */
    static find (autoConnect, name, options, failedCallback) {
        if (typeof autoConnect === 'undefined') {
            autoConnect = true;
        }

        options = options || {filters: [{namePrefix: name}], optionalServices: [Konashi._serviceUUID]};
        return new Promise((resolve, reject) => {
            let konashi = null;
            navigator.bluetooth.requestDevice(options).then(
                d => {
                    konashi = new Konashi(d);
                    if (autoConnect) {
                        konashi.connect(failedCallback).then(resolve, reject);
                    } else {
                        resolve(konashi);
                    }
                },
                e => {
                    const askedRetrieve = /retrieve services/;
                    if (askedRetrieve.test(e)) {
                        konashi.connect().then(resolve, reject); // TODO: Research reject activity
                    } else {
                        reject(e);
                    }
                }
            );
        });
    }

    /**
   * constructor
   * TODO: Set Handlers?
   *
   * @param {BluetoothDevice} device target BLE device
   */
    constructor (device) {
    /** BluetoothDevice */
        this._device = device;
        /** BluetoothGATTRemoteServer */
        this._gatt = null;
        /** BluetoothGATTService */
        this._service = null;
        /** Object<String, BluetoothGATTCharacteristic> */
        this._c12c = {};
        /** Object<String, Number> */
        this._state = {pioOutputs: 0, pwmModes: 0, hardPwmModes: 0};

        for (const key in Consts) {
            this[key] = Consts[key];
        }
    }

    /**
   * Connect to konashi
   *
   * Assign `_gatt` and `_service` properties when
   * the connection has been made.
   *
   * @param {function} failedCallback called when the connection fails
   * @returns {Promise<Konashi>} Promise resolved with this instance when connected
   */
    connect (failedCallback) {
        const that = this;
    
        return new Promise((resolve, reject) => {
            that._isConnectFailed = true;

            setTimeout(() => {
                if (that._isConnectFailed) {
                    failedCallback();
                    that._device.gatt.disconnect();
                }
            }, 3000);

            that._device.gatt
                .connect()
                .then(
                    gatt => {
                        // ChromeOS 接続OK
                        that._isConnectFailed = false;

                        that._gatt = gatt;
                        return gatt.getPrimaryService(Konashi._serviceUUID);
                    },
                    e => reject(e)
                )
                .then(
                    service => {
                        that._service = service;
                        const promises = [];
                        const keys = [];
                        for (const key in Konashi._c12cUUIDs) {
                            keys.push(key);
                        }
                        keys.forEach(label => {
                            promises.push(
                                that._service.getCharacteristic(Konashi._c12cUUIDs[label]).then(c => {
                                    that._c12c[label] = c;
                                    Promise.resolve();
                                })
                            );
                        });
                        return Promise.all(promises);
                    },
                    e => reject(e)
                )
                .then(
                    () => resolve(that),
                    e => reject(e)
                );
        });
    }

    /**
   * Return connection condition
   *
   * @returns {boolean} true if connected
   */
    isConnected () {
        let connected = false;
        if (this._gatt) {
            connected = this._gatt.connected;
        }
        return connected;
    }

    /**
   * Returns peripheral name
   *
   * @returns {string} peripheral name
   */
    name () {
        return this._device.name;
    }

    // { Digital I/O

    /**
   * Set konashi's pin mode
   *
   * @param {number} pin Consts.PIO[0-4]
   * @param {number} flag Consts.(INPUT|OUTPUT)
   * @returns {Promise<void>} Promise resolved when the value has been written
   */
    pinMode (pin, flag) {
        const that = this;
        return new Promise((resolve, reject) => {
            that._c12c.pioSetting.readValue().then(v => {
                let data = v.getUint8(0);
                if (flag == Consts.OUTPUT) {
                    data |= 0x01 << pin;
                } else {
                    data &= ~(0x01 << pin) & 0xff;
                }
                this._c12c.pioSetting.writeValue(new Uint8Array([data])).then(resolve, reject);
            });
        });
    }

    /**
   * Set konashi's pins mode
   *
   * @param {number} flags 0x29, 0: INPUT, 1 = OUTPUT, e.g. 00101001(pio0:OUT, pio4:OUT, pio5:OUT)
   * @returns {Promise<void>} Promise resolved when the value has been written
   */
    pinModeAll (flags) {
        const that = this;
        return new Promise((resolve, reject) => {
            if (flags >= 0x00 && flags <= 0x1f) {
                that._c12c.pioSetting.writeValue(new Uint8Array([flags])).then(resolve, reject);
            }
        });
    }

    /**
   * Set pullup mode
   *
   * @param {number} pin Consts.PIO[0-4]
   * @param {number} mode Consts.(PULLUP|NO_PULLS)
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    pinPullup (pin, mode) {
        const that = this;
        return new Promise((resolve, reject) => {
            that._c12c.pioPullUp.readValue().then(v => {
                let data = v.getUint8(0);
                if (mode == Consts.PULLUP) {
                    data |= 0x01 << pin;
                } else {
                    data &= ~(0x01 << pin);
                }
                this._c12c.pioPullUp.writeValue(new Uint8Array([data])).then(resolve, reject);
            });
        });
    }

    /**
   * Read a value of digital pin
   *
   * @param {number} pin Consts.PIO[0-4]
   * @returns {Promise<number>} Consts.(LOW|HIGH)
   */
    digitalRead (pin) {
        return this._c12c.pioInputNotification.readValue().then(buf => (buf.getUint8(0) >> pin) & 0x01);
    }

    /**
   * Set callback to Digital Input Notification.
   *
   * @param {Function<number>} callback arguments should be 0bXXXXXXXX.
   */
    startDigitalInputNotification (callback) {
        this.onReceived = event => {
            const buf = event.target.value;
            const value = buf.getUint8(0);
            callback(value);
        };
        const that = this;
        this._c12c.pioInputNotification
            .startNotifications()
            .then(() => {
                that._c12c.pioInputNotification.addEventListener('characteristicvaluechanged', that.onReceived);
            })
            .catch(error => {
                // Remove Event
                that._c12c.pioInputNotification.removeEventListener('characteristicvaluechanged', that.onReceived);
                console.error(error);
            });
    }

    stopDigitalInputNotification () {
        this._c12c.pioInputNotification.removeEventListener('characteristicvaluechanged', this.onReceived);
    }

    /**
   * Write value to a digital pin
   *
   * https://github.com/YUKAI/konashi-android-sdk/blob/master/konashi-android-sdk/src/main/java/com/uxxu/konashi/lib/dispatcher/PioStoreUpdater.java
   * https://github.com/YUKAI/konashi-android-sdk/blob/master/konashi-android-sdk/src/main/java/com/uxxu/konashi/lib/store/PioStore.java#L19
   *
   * @param {number} pin Consts.PIO[0-4]
   * @param {number} value Consts.(LOW|HIGH)
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    digitalWrite (pin, value) {
        if (value == Consts.HIGH) {
            this._state.pioOutputs |= 0x01 << pin;
        } else {
            this._state.pioOutputs &= ~(0x01 << pin) & 0xff;
        }
        return this._c12c.pioOutput.writeValue(new Uint8Array([this._state.pioOutputs]));
    }

    /**
   * Write value to digital pins
   *
   * @param {string} flags "0x29", 0: LOW, 1 = HIGH, e.g. 00101001(pio0:LOW, pio4:HIGH, pio5:HIGH)
   * @returns {Promise<void>} Promise resolved when the value has been written
   */
    digitalWriteAll (flags) {
        const that = this;
        return new Promise((resolve, reject) => {
            if (flags >= 0x00 && flags <= 0x1f) {
                that._state.pioOutputs = flags;
                that._c12c.pioOutput.writeValue(new Uint8Array([flags])).then(resolve, reject);
            }
        });
    }

    // Digital I/O }

    // { PWM

    /**
   * Set PWM mode
   *
   * @param {number} pin Consts.PIO[0-4]
   * @param {number} mode Consts.(KONASHI_PWM_ENABL|KONASHI_PWM_ENABLE_LED_MODE)
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    pwmMode (pin, mode) {
    // console.log('pwmMode: ' + pin + ' ' + mode);
        if (mode == Consts.KONASHI_PWM_ENABLE || mode == Consts.KONASHI_PWM_ENABLE_LED_MODE) {
            this._state.pwmModes |= 0x01 << pin;
        } else {
            this._state.pwmModes &= ~(0x01 << pin) & 0xff;
        }
        const that = this;
        const data = new Uint8Array([this._state.pwmModes]);
        if (mode == Consts.KONASHI_PWM_ENABLE_LED_MODE) {
            return this._c12c.pwmConfig.writeValue(data).then(() => that.pwmPeriod(pin, Consts.KONASHI_PWM_LED_PERIOD));
        }
        return this._c12c.pwmConfig.writeValue(data);
    }

    /**
   * Set the PWM cycle
   *
   * @param {number} pin Consts.PIO[0-4]
   * @param {number} period PWM period in microseconds
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    pwmPeriod (pin, period) {
        const data = new Uint8Array([
            pin,
            (period >> 24) & 0xff,
            (period >> 16) & 0xff,
            (period >> 8) & 0xff,
            (period >> 0) & 0xff
        ]);
        return this._c12c.pwmParameter.writeValue(data);
    }

    /**
   * Set the duty cycle
   *
   * @param {number} pin Consts.PIO[0-4]
   * @param {number} duty Please specify the units as microseconds (us) in 32bits.
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    pwmDuty (pin, duty) {
        duty = parseInt(duty, 10);
        const data = new Uint8Array([
            pin,
            (duty >> 24) & 0xff,
            (duty >> 16) & 0xff,
            (duty >> 8) & 0xff,
            (duty >> 0) & 0xff
        ]);
        // console.log('pwmDuty: ' + pin + ' ' + duty);
        return this._c12c.pwmDuty.writeValue(data);
    }

    pwmLedDrive (pin, ratio) {
        ratio = Math.min(100.0, Math.max(0.0, ratio));
        const duty = (Consts.KONASHI_PWM_LED_PERIOD * ratio) / 100;
        return this.pwmDuty(pin, duty);
    }

    /**
   * Write PWM ratio
   *
   * @param {number} pin Consts.PIO[0-4]
   * @param {number} ratio (0-100)
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    pwmWrite (pin, ratio) {
        ratio = Math.min(100, Math.max(0, ratio));
        const duty = (Consts.KONASHI_PWM_LED_PERIOD * ratio) / 100;
        return this.pwmDuty(pin, duty);
    }

    // PWM }

    // { Hard PWM

    /**
   * Set Hard PWM mode
   *
   * @param {number} pin Consts.PWM[0-3]
   * @param {number} mode Consts.(KONASHI_PWM_ENABL)
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    hardPwmMode (pin, mode) {
        if (mode == Consts.KONASHI_PWM_ENABLE) {
            this._state.hardPwmModes |= 0x01 << pin;
        } else {
            this._state.hardPwmModes &= ~(0x01 << pin) & 0xff;
        }
        const data = new Uint8Array([this._state.hardPwmModes]);
        return this._c12c.hardPwmConfig.writeValue(data);
    }

    /**
   * Set konashi's hard PWM mode
   *
   * @param {number} flags bit flags of PWM pins to enable (0x00-0x0f)
   * @returns {Promise<void>} Promise resolved when the value has been written
   */
    hardPwmModeAll (flags) {
        const that = this;
        return new Promise((resolve, reject) => {
            if (flags >= 0x00 && flags <= 0x0f) {
                that._c12c.hardPwmConfig.writeValue(new Uint8Array([flags])).then(resolve, reject);
            }
        });
    }

    /**
   * Set the duty cycle
   *
   * @param {number} pin Consts.PWM[0-4]
   * @param {number} duty Please specify the units as microseconds (us) in 16bits.
   * @returns {Promise<Void>} Promise resolved when the value has been written
   */
    hardPwmDuty (pin, duty) {
        const data = new Uint8Array([pin, (duty >> 8) & 0xff, (duty >> 0) & 0xff]);
        return this._c12c.hardPwmDuty.writeValue(data);
    }

    /**
   * Set the duty cycle at all
   *
   * @param {number} duty0 PWM0 duty in microseconds
   * @param {number} duty1 PWM1 duty in microseconds
   * @param {number} duty2 PWM2 duty in microseconds
   * @param {number} duty3 PWM3 duty in microseconds
   * @returns {Promise<void>} Promise resolved when the value has been written
   */
    hardPwmDutyAll (duty0, duty1, duty2, duty3) {
        const data = new Uint8Array([
            (duty0 >> 8) & 0xff,
            (duty0 >> 0) & 0xff,
            (duty1 >> 8) & 0xff,
            (duty1 >> 0) & 0xff,
            (duty2 >> 8) & 0xff,
            (duty2 >> 0) & 0xff,
            (duty3 >> 8) & 0xff,
            (duty3 >> 0) & 0xff
        ]);

        return this._c12c.hardPwmDutyAll.writeValue(data);
    }

    /**
   *
   * @param {number} pin Consts.PWM[0-4]
   * @param {number} duty [us]
   * @param {number} duration 0 - 65535[msec]
   * @returns {Promise<void>} Promise resolved when the value has been written
   */
    hardPwmDutyTime (pin, duty, duration) {
        const data = new Uint8Array([
            pin,
            (duty >> 8) & 0xff,
            (duty >> 0) & 0xff,
            (duration >> 8) & 0xff,
            (duration >> 0) & 0xff
        ]);
        return this._c12c.hardPwmDutyTime.writeValue(data);
    }

    // Hard PWM}

    // { Hardware Control

    /**
   * Disconnect
   *
   * @returns {void} return value of gatt.disconnect()
   */
    disconnect () {
        return this._gatt.disconnect();
    }

    /**
   * Reset software
   *
   * @returns {Promise<void>} Promise resolved when the reset command has been written
   */
    softwareReset () {
        this._state.pioOutputs = 0x00;
        this._state.pwmModes = 0x00;
        this._state.hardPwmModes = 0x00;

        return this._c12c.softwareReset.writeValue(new Uint8Array([1]));
    }

    /**
   * Reset hardware
   *
   * @returns {Promise<void>} Promise resolved when the reset command has been written
   */
    hardwareReset () {
        return this._c12c.hardwareReset.writeValue(new Uint8Array([1]));
    }

    /**
   * Read battery level
   *
   * @returns {Promise<number>} Promise resolved with the battery level
   */
    batteryLevelRead () {
        return new Promise(resolve => {
            this._gatt
                .getPrimaryService('battery_service')
                .then(service => service.getCharacteristic('battery_level'))
                .then(v => {
                    resolve(new Uint8Array(v)[0]);
                });
        });
    }
    // Hardware Control }
}

// module.exports = Konashi;
exports.funcs = Konashi;
exports.consts = Consts;
